import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api from '@/utils/api';
import { Eye, EyeOff, User, Lock, ArrowRight, Building, Download, ArrowLeft } from 'lucide-react';

export default function CustomerLogin({ onCustomerLogin }) {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    login_id: '',
    password: ''
  });

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        toast.success('App installed successfully!');
      }
      setDeferredPrompt(null);
      setShowInstallButton(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/customer-portal/login', formData);
      const { token, customer } = response.data;
      
      onCustomerLogin(customer, token);
      toast.success(`Welcome ${customer.name}!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid Login ID or Password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Branding (Desktop Only) */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&q=80)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-teal-900/90 via-emerald-800/85 to-slate-900/90" />
        
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-4">
            <div className="bg-white p-3 rounded-2xl shadow-2xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_proptech-admin-suite/artifacts/jcoqxxo3_logo%20%281%29.png" 
                alt="Ganesa" 
                className="w-16 h-16 object-contain"
              />
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Ganesa</h1>
              <p className="text-emerald-300 font-medium">Customer Portal</p>
            </div>
          </div>

          <div className="space-y-8">
            <div>
              <h2 className="text-4xl font-bold leading-tight">
                अपनी बुकिंग<br />
                <span className="text-emerald-400">ट्रैक</span> करें<br />
                कभी भी, कहीं भी
              </h2>
              <p className="mt-6 text-lg text-emerald-100/80 max-w-md">
                अपने प्लॉट की जानकारी, किस्त की स्थिति और पेमेंट हिस्ट्री देखें - सब एक जगह।
              </p>
            </div>

            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20">
                <p className="font-semibold">बुकिंग विवरण</p>
                <p className="text-sm text-emerald-200">अपनी सभी प्रॉपर्टी बुकिंग देखें</p>
              </div>
              <div className="p-4 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20">
                <p className="font-semibold">पेमेंट ट्रैकिंग</p>
                <p className="text-sm text-emerald-200">किस्त और बकाया राशि ट्रैक करें</p>
              </div>
              <div className="p-4 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20">
                <p className="font-semibold">इनवॉइस डाउनलोड</p>
                <p className="text-sm text-emerald-200">अपनी रसीदें प्रिंट करें</p>
              </div>
            </div>
          </div>

          <p className="text-emerald-200/60 text-sm">
            © 2024 Ganesa Homestate. All rights reserved.
          </p>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex flex-col min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 lg:justify-center">
        
        {/* Mobile Header */}
        <div className="lg:hidden relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-teal-600 via-emerald-700 to-teal-800" />
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 right-0 w-40 h-40 bg-white rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2"></div>
          </div>
          
          <div className="relative z-10 px-6 pt-8 pb-12 text-center text-white">
            <div className="inline-block bg-white p-4 rounded-3xl shadow-2xl mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_proptech-admin-suite/artifacts/jcoqxxo3_logo%20%281%29.png" 
                alt="Ganesa" 
                className="w-16 h-16 object-contain"
              />
            </div>
            <h1 className="text-2xl font-bold tracking-tight">Customer Portal</h1>
            <p className="text-emerald-200 text-sm mt-1">Ganesa Homestate</p>
            
            {showInstallButton && (
              <button
                onClick={handleInstallClick}
                data-testid="pwa-install-button"
                className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium border border-white/30 hover:bg-white/30 transition-all"
              >
                <Download size={16} />
                Install App
              </button>
            )}
          </div>
          
          <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-br from-slate-50 to-slate-100 rounded-t-[2rem]"></div>
        </div>

        {/* Form Container */}
        <div className="flex-1 flex items-start lg:items-center justify-center px-6 py-6 lg:py-0">
          <div className="w-full max-w-md">
            {/* Back to Admin Link */}
            <button
              onClick={() => navigate('/login')}
              className="mb-4 flex items-center gap-2 text-slate-500 hover:text-emerald-600 transition-colors text-sm"
            >
              <ArrowLeft size={16} />
              Admin Login
            </button>

            {/* Form Card */}
            <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-8 border border-slate-100" data-testid="customer-login-card">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-2xl mb-4">
                  <Building className="text-emerald-600" size={32} />
                </div>
                <h2 className="text-xl sm:text-2xl font-bold text-slate-800">
                  Customer Login
                </h2>
                <p className="text-slate-500 mt-1 text-sm">
                  अपना Login ID और Password डालें
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
                <div className="space-y-1.5">
                  <Label className="text-slate-700 font-medium text-sm">Login ID</Label>
                  <div className="relative">
                    <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      data-testid="login-id-input"
                      placeholder="GH123456"
                      value={formData.login_id}
                      onChange={(e) => setFormData({ ...formData, login_id: e.target.value.toUpperCase() })}
                      required
                      autoComplete="off"
                      className="h-12 pl-12 rounded-xl border-slate-200 focus:border-emerald-500 focus:ring-emerald-500 font-mono uppercase"
                    />
                  </div>
                  <p className="text-xs text-slate-400">आपको यह ID बुकिंग के समय दी गई थी</p>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-slate-700 font-medium text-sm">Password</Label>
                  <div className="relative">
                    <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      data-testid="password-input"
                      placeholder="••••••"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value.toUpperCase() })}
                      required
                      autoComplete="new-password"
                      className="h-12 pl-12 pr-12 rounded-xl border-slate-200 focus:border-emerald-500 focus:ring-emerald-500 font-mono uppercase"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  data-testid="customer-login-button"
                  disabled={loading}
                  className="w-full h-12 rounded-xl bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white font-semibold shadow-lg shadow-emerald-500/25 transition-all duration-300 group"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                      Please wait...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      Login
                      <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                    </span>
                  )}
                </Button>
              </form>

              {/* Help Section */}
              <div className="mt-6 p-4 bg-amber-50 rounded-xl border border-amber-200">
                <p className="text-sm text-amber-800 font-medium">Login ID कहाँ मिलेगा?</p>
                <p className="text-xs text-amber-600 mt-1">
                  आपका Login ID और Password बुकिंग के समय दिया गया था। अगर याद नहीं है तो ऑफिस से संपर्क करें।
                </p>
              </div>
            </div>

            {/* Mobile Footer */}
            <div className="lg:hidden text-center mt-6 pb-4">
              <p className="text-slate-400 text-xs">
                © 2024 Ganesa Homestate
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
